import React from 'react';
import GameModal from '../GameModal/GameModal.tsx';

import './HowToPlayModal.css';
import {EnteredGameRow, ExampleGameRow} from '../../games/weaver/GameRow.tsx';
import { GameType } from '../../types/GameType.ts';

interface HowToPlayModalProps {
  gameType: GameType;
  isOpen: boolean;
  onClose: () => void;
}

export default function HowToPlayModal(props: HowToPlayModalProps): React.ReactElement {
  const weaverModalContent = (
    <div id='howToPlayModalContent'>
      <div className='howToPlayHistorySection'>
        This game is called a "word ladder" and was invented by Lewis Carroll in 1877.
      </div>
      <div className='howToPlayRulesSection'>
        <div className='howToPlayExampleTitle'>
          Rules
        </div>
        <div className='howToPlaySentenceBlock'>
          Weave your way from the start word to the end word.
        </div>
        <div className='howToPlaySentenceBlock'>
          Each word you enter <b>can only change 1 letter</b> from the word above it.
        </div>
      </div>
      <div className='howToPlayExampleSection'>
        <div className='howToPlayExampleTitle'>
          Example
        </div>
        <div className='howToPlaySentenceBlock'>
          <b>EAST</b> is the start word, <b>WEST</b> is the goal
        </div>
        <div className='howToPlayExampleGameRow'>
          <ExampleGameRow word='east' />
        </div>
        <div className='howToPlaySentenceBlock'>
          We can change <b>E</b> to <b>V</b> to make <b>V</b>AST
        </div>
        <div className='howToPlayExampleGameRow'>
          <EnteredGameRow 
            word='vast'
            previousWord='east'
            endWord='west'
            rowLength={4}
            gameType={props.gameType}
          />
        </div>
        <div className='howToPlaySentenceBlock'>
          Now we change <b>A</b> to <b>E</b> to make V<b>E</b>ST
        </div>
        <div className='howToPlayExampleGameRow'>
          <EnteredGameRow 
            word='vest'
            previousWord='vast'
            endWord='west'
            rowLength={4}
            gameType={props.gameType}
          />
        </div>
        <div className='howToPlaySentenceBlock'>
          And change <b>V</b> to <b>W</b> to make <b>W</b>EST
        </div>
        <div className='howToPlayExampleGameRow'>
          <EnteredGameRow 
            word='west'
            previousWord='vest'
            endWord='west'
            rowLength={4}
            gameType={props.gameType}
          />
        </div>
        <div className='howToPlaySentenceBlock'>
          Done!
        </div>
      </div>
    </div>
  );

  const weaverXModalContent = (
    <div id='howToPlayModalContent' className='weaverX'>
      <div className='howToPlayHistorySection'>
        The classic "word ladder" game with a twist: you can add or remove letters.
      </div>
      <div className='howToPlayRulesSection'>
        <div className='howToPlayExampleTitle'>
          Rules
        </div>
        <div className='howToPlaySentenceBlock'>
          Weave your way from the start word to the end word.
        </div>
        <div className='howToPlaySentenceBlock'>
          Each word you enter can either <b>change 1 letter</b> or <b>add 1 letter</b> or <b>remove 1 letter</b> from the word above it.
        </div>
      </div>
      <div className='howToPlayExampleSection'>
        <div className='howToPlayExampleTitle'>
          Example 1
        </div>
        <div className='howToPlaySentenceBlock'>
          <b>RED</b> is the start word, <b>GREEN</b> is the goal
        </div>
        <div className='howToPlayExampleGameRow'>
          <ExampleGameRow word='red' />
        </div>
        <div className='howToPlaySentenceBlock'>
          We can add <b>E</b> to make RE<b>E</b>D
        </div>
        <div className='howToPlayExampleGameRow'>
          <EnteredGameRow 
            word='reed'
            previousWord='red'
            endWord='green'
            rowLength={4}
            gameType={props.gameType}
          />
        </div>
        <div className='howToPlaySentenceBlock'>
          Now we add <b>G</b> to make <b>G</b>REED
        </div>
        <div className='howToPlayExampleGameRow'>
          <EnteredGameRow 
            word='greed'
            previousWord='reed'
            endWord='green'
            rowLength={5}
            gameType={props.gameType}
          />
        </div>
        <div className='howToPlaySentenceBlock'>
          And change <b>D</b> to <b>N</b> to make GREE<b>N</b>
        </div>
        <div className='howToPlayExampleGameRow'>
          <EnteredGameRow 
            word='green'
            previousWord='greed'
            endWord='green'
            rowLength={5}
            gameType={props.gameType}
          />
        </div>
        <div className='howToPlaySentenceBlock'>
          Done!
        </div>
      </div>
      <div className='howToPlayExampleSection'>
        <div className='howToPlayExampleTitle'>
          Example 2
        </div>
        <div className='howToPlaySentenceBlock'>
          <b>COLD</b> is the start word, <b>HOT</b> is the goal
        </div>
        <div className='howToPlayExampleGameRow'>
          <ExampleGameRow word='cold' />
        </div>
        <div className='howToPlaySentenceBlock'>
          We can remove <b>L</b> to make COD
        </div>
        <div className='howToPlayExampleGameRow'>
          <EnteredGameRow 
            word='cod'
            previousWord='cold'
            endWord='hot'
            rowLength={4}
            gameType={props.gameType}
          />
        </div>
        <div className='howToPlaySentenceBlock'>
          Now we change <b>D</b> to <b>T</b> to make CO<b>T</b>
        </div>
        <div className='howToPlayExampleGameRow'>
          <EnteredGameRow 
            word='cot'
            previousWord='cod'
            endWord='hot'
            rowLength={5}
            gameType={props.gameType}
          />
        </div>
        <div className='howToPlaySentenceBlock'>
          And change <b>C</b> to <b>H</b> to make <b>H</b>OT
        </div>
        <div className='howToPlayExampleGameRow'>
          <EnteredGameRow 
            word='hot'
            previousWord='cot'
            endWord='hot'
            rowLength={5}
            gameType={props.gameType}
          />
        </div>
        <div className='howToPlaySentenceBlock'>
          Done!
        </div>
      </div>
    </div>
  );

  const subtextModalContent = (
    <div id='howToPlayModalContent' className='subtext'>
      <div className='howToPlayHistorySection'>
        Create words using letters from the given word.
      </div>
      <div className='howToPlayRulesSection'>
        <div className='howToPlayExampleTitle'>
          Rules
        </div>
        <div className='howToPlaySentenceBlock'>
          Words must contain at least 4 letters.
        </div>
        <div className='howToPlaySentenceBlock'>
          Each letter can only be used once.
        </div>
      </div>
      <div>
        <div className='howToPlayExampleTitle'>
          Points
        </div>
        <div className='howToPlaySentenceBlock'>
          Points follow a triangular pattern:<br />
          &nbsp;&nbsp;&nbsp;&nbsp;• 4-letter word = 1 pt<br />
          &nbsp;&nbsp;&nbsp;&nbsp;• 5-letter word = 3 pts<br />
          &nbsp;&nbsp;&nbsp;&nbsp;• 6-letter word = 6 pts<br />
          &nbsp;&nbsp;&nbsp;&nbsp;• 7-letter word = 10 pts<br />
          &nbsp;&nbsp;&nbsp;&nbsp;• And so on...
        </div>
        <div className='howToPlaySentenceBlock'>
          Bonus points:<br />
          &nbsp;&nbsp;&nbsp;&nbsp;• +2 pts for longest possible words<br />
          &nbsp;&nbsp;&nbsp;&nbsp;• +5 pts for anagrams* (all letters used)<br />
        </div>
        <div className='howToPlaySentenceBlock'>
          *Not all games have anagrams.
        </div>
      </div>
    </div>
  );

  return (
    <GameModal
      title='How To Play'
      content={props.gameType === GameType.SUBTEXT ? subtextModalContent : props.gameType === GameType.WEAVER_X ? weaverXModalContent : weaverModalContent}
      isOpen={props.isOpen}
      onClose={props.onClose}
    />
  );
}